import { Invitation, Registerer, URI, UserAgent, type UserAgentOptions, UserAgentState } from 'sip.js';
import { getPlatformString, getRevision, isElectron } from '@/utils/clientUtils';
import { RegistererState, TransportState } from 'sip.js/lib/api';
import { HaTransport, type HaTransportOptions, type ServerRotation } from '@/libraries/sip/haTransport';
import type { IncomingResponse } from 'sip.js/lib/core';
import { Subscriber } from 'sip.js';
import { computed, ref } from 'vue';
import { useUserStore } from '@/stores/useUserStore';
import { useSessionStore } from '@/stores/useSessionStore';
import { useClientSideSettingsStore } from '@/stores/useClientSideSettingsStore';
import { useNotificationStore } from '@/stores/useNotificationStore.ts';
import { useI18n } from 'vue-i18n';

export type PhoneStatusType = 'UNINITIALIZED' | 'INITIALIZING' | 'INITIALIZED';

export const usePhoneConnection = (onInvite: (invitation: Invitation) => void) => {
  let keepConnected = false;
  let userAgent: UserAgent | undefined;
  let registerer: Registerer | null = null;
  let keepAliveInterval: NodeJS.Timeout | null = null;
  const subscribers: Subscriber[] = [];
  const tryingToReconnect = ref(false);
  const phoneStatus = ref<PhoneStatusType>('UNINITIALIZED');

  let reconnecting = false;

  let optionsPingTimer: NodeJS.Timeout | null = null;
  let pendingPingTimeout: NodeJS.Timeout | null = null;
  let missedPings = 0;

  let last403AttemptTime: number | null = null;
  let forbidden403RetryCount = 0;
  let is403RetryExhausted = false;

  const $session = useSessionStore();
  const $clientSettingsStore = useClientSideSettingsStore();
  const notificationStore = useNotificationStore();
  const { t } = useI18n();

  const RETRY_CONFIG = {
    RETRY_INTERVAL_MS: 60_000,
    MAX_RETRY_ATTEMPTS: 15,
  } as const;

  const PING_CONFIG = {
    // Steady-state cadence while the path is healthy (also keeps NAT/proxy warm).
    STEADY_INTERVAL_MS: 30_000,
    // Faster cadence once a ping goes unanswered, to confirm/clear quickly.
    FAST_INTERVAL_MS: 5_000,
    // How long to wait for any SIP response before counting a ping as missed.
    RESPONSE_TIMEOUT_MS: 5_000,
    // Consecutive missed pings before we declare the socket dead and rebuild.
    MAX_MISSED_PINGS: 3,
  } as const;

  const FAILOVER_SERVERS: string[] = SIP_FAILOVER_SERVERS
    ? SIP_FAILOVER_SERVERS.split(',')
        .map((s) => s.trim())
        .filter((s) => s.startsWith('wss://') || s.startsWith('ws://'))
    : [];

  const serverRotation: ServerRotation = { index: 0 };

  const buildServerList = (domain: string): string[] => [`wss://${domain}:4443`, ...FAILOVER_SERVERS];

  type RetryState = 'not-in-retry' | 'waiting' | 'ready' | 'exhausted';

  const evaluateRetry = (): RetryState => {
    if (is403RetryExhausted) return 'exhausted';
    if (last403AttemptTime === null) return 'not-in-retry';
    if (Date.now() - last403AttemptTime < RETRY_CONFIG.RETRY_INTERVAL_MS) return 'waiting';
    return 'ready';
  };

  const handleRetryExhaustion = (): void => {
    console.error(`Phone keepalive: 403 retry limit reached (${RETRY_CONFIG.MAX_RETRY_ATTEMPTS} minutes). Stopping until page reload.`);
    is403RetryExhausted = true;
    last403AttemptTime = null;

    if (keepAliveInterval) {
      clearInterval(keepAliveInterval);
      keepAliveInterval = null;
    }

    notificationStore.setSnackBar({
      message: t('phone.registration.retry_exhausted'),
      variant: 'error',
      timeout: -1,
    });
  };

  const executeRetryRegistration = (): void => {
    console.log(`Phone keepalive: 403 retry attempt ${forbidden403RetryCount}/${RETRY_CONFIG.MAX_RETRY_ATTEMPTS}`);
    last403AttemptTime = Date.now();

    registerer!.register({
      requestDelegate: {
        onAccept: handleRetrySuccess,
        onReject: handleRetryReject,
      },
    });
  };

  const handleRetrySuccess = (): void => {
    console.log('Registering succeeded after 403 retry.');
    resetRetryState();
    phoneStatus.value = 'INITIALIZED';

    if (isElectron()) {
      $clientSettingsStore.applyHotkeySetting();
    }
  };

  const handleRetryReject = (retryResponse: IncomingResponse): void => {
    console.error(`Phone register: 403 retry attempt ${forbidden403RetryCount} rejected with ${retryResponse.message.statusCode}`);
  };

  const resetRetryState = (): void => {
    last403AttemptTime = null;
    forbidden403RetryCount = 0;
  };

  const triggerReconnect = (userAgentOptions: Partial<UserAgentOptions>): void => {
    void initializeNewUserAgentAndRegister(userAgentOptions).catch((e) =>
      console.warn('Phone: reconnect attempt failed, will retry on next keepalive tick.', e)
    );
  };

  const stopOptionsHeartbeat = (): void => {
    if (optionsPingTimer) {
      clearTimeout(optionsPingTimer);
      optionsPingTimer = null;
    }
    if (pendingPingTimeout) {
      clearTimeout(pendingPingTimeout);
      pendingPingTimeout = null;
    }
    missedPings = 0;
  };

  const scheduleNextPing = (delayMs: number, userAgentOptions: Partial<UserAgentOptions>): void => {
    if (optionsPingTimer) clearTimeout(optionsPingTimer);
    optionsPingTimer = setTimeout(() => sendOptionsPing(userAgentOptions), delayMs);
  };

  const startOptionsHeartbeat = (userAgentOptions: Partial<UserAgentOptions>): void => {
    stopOptionsHeartbeat();
    scheduleNextPing(PING_CONFIG.STEADY_INTERVAL_MS, userAgentOptions);
  };

  const onPingMissed = (userAgentOptions: Partial<UserAgentOptions>): void => {
    missedPings++;
    if (missedPings >= PING_CONFIG.MAX_MISSED_PINGS) {
      console.error(`OPTIONS heartbeat: ${missedPings} pings unanswered, connection is dead. Rotating + reregistering...`);
      stopOptionsHeartbeat();

      const opts = userAgentOptions.transportOptions as HaTransportOptions | undefined;
      if (opts?.servers && opts.servers.length > 1) {
        serverRotation.index = (serverRotation.index + 1) % opts.servers.length;
        console.warn(`OPTIONS heartbeat: forced rotation to server[${serverRotation.index}] = ${opts.servers[serverRotation.index]}`);
      }

      triggerReconnect(userAgentOptions);
      return;
    }
    console.warn(`OPTIONS heartbeat: ping missed (${missedPings}/${PING_CONFIG.MAX_MISSED_PINGS}), escalating cadence.`);
    scheduleNextPing(PING_CONFIG.FAST_INTERVAL_MS, userAgentOptions);
  };

  const sendOptionsPing = (userAgentOptions: Partial<UserAgentOptions>): void => {
    if (!keepConnected || reconnecting || userAgent == null || userAgentOptions.uri == null) return;

    const aor = userAgentOptions.uri;
    let answered = false;

    const onResponse = (): void => {
      if (answered) return;
      answered = true;
      if (pendingPingTimeout) {
        clearTimeout(pendingPingTimeout);
        pendingPingTimeout = null;
      }
      if (missedPings > 0) {
        console.log('OPTIONS heartbeat: path recovered, back to steady cadence.');
      }
      missedPings = 0;
      scheduleNextPing(PING_CONFIG.STEADY_INTERVAL_MS, userAgentOptions);
    };

    if (pendingPingTimeout) clearTimeout(pendingPingTimeout);
    pendingPingTimeout = setTimeout(() => {
      if (answered) return;
      pendingPingTimeout = null;
      onPingMissed(userAgentOptions);
    }, PING_CONFIG.RESPONSE_TIMEOUT_MS);

    try {
      const core = userAgent.userAgentCore;
      // Any response proves liveness, so every delegate callback clears the miss.
      const message = core.makeOutgoingRequestMessage('OPTIONS', aor, aor, aor, {});
      core.request(message, {
        onAccept: onResponse,
        onProgress: onResponse,
        onRedirect: onResponse,
        onReject: onResponse,
        onTrying: onResponse,
      });
    } catch (e) {
      console.error('OPTIONS heartbeat: failed to send ping:', e);
      if (pendingPingTimeout) {
        clearTimeout(pendingPingTimeout);
        pendingPingTimeout = null;
      }
      onPingMissed(userAgentOptions);
    }
  };

  const abandonConnection = (ua: UserAgent): void => {
    void ua.stop().catch(() => {
      /* expected to fail/hang on a dead socket; ignored */
    });
  };

  const initializeNewUserAgentAndRegister = async (userAgentOptions: Partial<UserAgentOptions>): Promise<void> => {
    if (reconnecting) {
      console.log('Phone: (re)initialization already in progress, skipping duplicate trigger.');
      return;
    }
    reconnecting = true;
    tryingToReconnect.value = true;
    console.log('(Re)initializing Useragent');

    last403AttemptTime = null;
    forbidden403RetryCount = 0;
    is403RetryExhausted = false;

    try {
      stopOptionsHeartbeat();

      if (userAgent && userAgent.state !== UserAgentState.Stopped) {
        // Bring up a brand-new WSS instead of blocking on a graceful close of
        // the (possibly dead) old one — see abandonConnection().
        subscribers?.forEach((subscriber) => subscriber.unsubscribe());
        abandonConnection(userAgent);
      }

      userAgent = new UserAgent(userAgentOptions);
      registerer = new Registerer(userAgent);

      const thisUserAgent = userAgent;
      thisUserAgent.transport.stateChange.addListener((state) => {
        if (state !== TransportState.Disconnected) return;
        if (!keepConnected) return;
        if (userAgent !== thisUserAgent) return;
        console.warn('Phone: transport disconnected unexpectedly; reconnecting...');
        triggerReconnect(userAgentOptions);
      });

      if (keepAliveInterval) {
        console.log('Phone keepalive: Clearing old interval');
        clearInterval(keepAliveInterval);
        keepAliveInterval = null;
      }

      console.log('Phone keepalive: Setting new interval');
      keepAliveInterval = setInterval(() => {
        if (keepConnected) {
          if (userAgent == null) {
            console.error('Phone keepalive: Useragent is null. Reinitializing it');
            triggerReconnect(userAgentOptions);
          } else {
            let isConnected;
            try {
              isConnected = userAgent.isConnected();
              if (!isConnected) {
                console.error('Phone keepalive: Useragent not connected!');
              }
            } catch (e) {
              console.error('Phone keepalive: Useragent.isConnected threw error:' + e);
              isConnected = false;
            }

            if (isConnected && (registerer === null || registerer.state != RegistererState.Registered)) {
              const retryState = evaluateRetry();

              if (retryState === 'ready') {
                forbidden403RetryCount++;
                if (forbidden403RetryCount > RETRY_CONFIG.MAX_RETRY_ATTEMPTS) handleRetryExhaustion();
                else executeRetryRegistration();
              } else if (retryState === 'not-in-retry') {
                console.error('Phone keepalive: Useragent is connected, but registerer is not registered!');
                isConnected = false;
              }
            }

            if (!isConnected) {
              console.error("Phone keepalive: Something's wrong, reinitializing Useragent and reregistering...");
              triggerReconnect(userAgentOptions);
            } else if (registerer?.state === RegistererState.Registered) {
              console.log('Phone keepalive: Useragent is connected and registerer is registered, by the way. yay!');
            } else {
              console.log('Phone keepalive: Useragent is connected but registerer is not yet registered (403 retry in progress).');
            }
          }
        } else {
          console.log("Phone keepalive: Useragent isn't keeping connected.");
        }
      }, 5_000);

      try {
        // Bounded by the watchdog (~4s); rejects fast on a dead node instead of
        // hanging.
        await userAgent.start();
      } catch (e) {
        if (keepConnected) setTimeout(() => triggerReconnect(userAgentOptions), 0);
        throw e;
      }

      startOptionsHeartbeat(userAgentOptions);

      await new Promise<void>((resolve, reject) => {
        console.log('Registering...');
        registerer!.register({
          requestDelegate: {
            onAccept: () => {
              console.log('Registering succeeded.');
              resolve();
            },

            onReject: (response: IncomingResponse) => {
              console.error('Phone register rejected with response ' + response.message);
              if (response.message.statusCode === 403) {
                console.error('Phone register: 403 Forbidden. Will retry every 1 minute for up to 15 minutes.');
                last403AttemptTime = Date.now();
                forbidden403RetryCount = 0;
                reject('Error while registering phone: 403 Forbidden');
              } else {
                reject('Error while registering phone');
              }
            },
          },
        });
      });

      tryingToReconnect.value = false;
      phoneStatus.value = 'INITIALIZED';
      if (isElectron()) {
        $clientSettingsStore.applyHotkeySetting();
      }
    } finally {
      reconnecting = false;
    }
  };

  const login = async (): Promise<void> => {
    phoneStatus.value = 'INITIALIZING';
    console.log('PHONE LOGGED IN');

    const $userStore = useUserStore();
    const loggedInUser = $userStore._loggedInUser;
    const domain = $session._session.domain;
    const extensionNumber = loggedInUser.extension?.extension;
    const authorizationPassword = loggedInUser.extension?.password;

    if (!domain || !extensionNumber || !authorizationPassword) {
      console.error(
        `One of the required values was not available. Domain: ${domain}, User extension ${extensionNumber}, Password available: ${!!authorizationPassword}`
      );
      phoneStatus.value = 'UNINITIALIZED';
      return Promise.reject('Phone creation was not possible');
    }
    keepConnected = true;
    const uri = new URI(undefined, extensionNumber, domain);
    const servers = buildServerList(domain);

    const userAgentOptions: Partial<UserAgentOptions> = {
      userAgentString: `WTG Cloud Pure Client / ${getRevision()} (${getPlatformString()})`,
      authorizationPassword,
      transportConstructor: HaTransport,
      transportOptions: {
        // `server` is the base/initial URL; `servers` + `rotation` drive the
        // actual per-attempt target so failover advances to the other VM.
        server: servers[serverRotation.index % servers.length],
        servers,
        rotation: serverRotation,
        traceSip: true,
        // Watchdog force-closes a stuck connect at ~4s (HaTransport); connectionTimeout
        // is SIP.js' own backstop. A dead node (no TCP RST) is abandoned fast instead
        // of hanging on the OS TCP timeout, then the reconnect loop rotates + retries.
        watchdogTimeout: 4000,
        connectionTimeout: 4,
        keepAliveInterval: 10,
        keepAliveDebounce: 5,
      } as HaTransportOptions,
      uri,
      viaHost: '192.168.0.116',
      delegate: {
        onInvite: (invitation: Invitation) => {
          onInvite(invitation);
        },
      },
      sessionDescriptionHandlerFactoryOptions: {
        peerConnectionConfiguration: {
          iceServers: [],
        },
      },
      noAnswerTimeout: 5 * 60,
    };

    console.log('Initializing new user agent and registering...');
    await initializeNewUserAgentAndRegister(userAgentOptions);
    console.log('Done initializing new user agent and registering.');
    phoneStatus.value = 'INITIALIZED';
    if (isElectron()) {
      $clientSettingsStore.applyHotkeySetting();
    }
  };

  const tryAsync = async (label: string, fn: () => Promise<unknown>) => {
    try {
      await fn();
    } catch (e) {
      console.warn(`Failed to ${label} during logout:`, e);
    }
  };

  const logout = async () => {
    if (phoneStatus.value === 'UNINITIALIZED') return;
    console.log('Phone logging out...');
    keepConnected = false;

    last403AttemptTime = null;
    forbidden403RetryCount = 0;
    is403RetryExhausted = false;

    if (isElectron()) {
      console.log('Unsetting call shortcut');
      window.wtgPhoneApi.setCallShortcut({ shortcut: null });
    }

    if (keepAliveInterval) {
      clearInterval(keepAliveInterval);
      keepAliveInterval = null;
    }
    stopOptionsHeartbeat();

    await Promise.all(subscribers.map((sub) => tryAsync('unsubscribe', () => sub.unsubscribe())));

    await tryAsync('unregister', () => registerer!.unregister());
    await tryAsync('stop user agent', () => userAgent!.stop());

    phoneStatus.value = 'UNINITIALIZED';
  };

  const getUserAgent = () => userAgent;

  const isOffline = computed((): boolean => tryingToReconnect.value);

  return {
    phoneStatus,
    isOffline,
    login,
    logout,
    getUserAgent,
  };
};
